import { createContext, useContext, useState, useCallback, ReactNode } from "react";

export interface FlipEntry {
  id: number;
  item: string;
  buyPrice: number;
  sellPrice: number;
  fees: number;
  profit: number;
  platform: string;
  date: string;
  status: "sold" | "listed" | "in_hand";
}

const STORAGE_KEY = "trackly_flip_history";

function loadEntries(): FlipEntry[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function persistEntries(entries: FlipEntry[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

interface FlipStorageContextType {
  entries: FlipEntry[];
  addEntry: (entry: Omit<FlipEntry, "id" | "date">) => void;
  deleteEntry: (id: number) => void;
}

const FlipStorageContext = createContext<FlipStorageContextType | null>(null);

export function FlipStorageProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<FlipEntry[]>(loadEntries);

  const addEntry = useCallback((entry: Omit<FlipEntry, "id" | "date">) => {
    const newEntry: FlipEntry = {
      ...entry,
      id: Date.now(),
      date: new Date().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    };
    setEntries((prev) => {
      const updated = [newEntry, ...prev];
      persistEntries(updated);
      return updated;
    });
  }, []);

  const deleteEntry = useCallback((id: number) => {
    setEntries((prev) => {
      const updated = prev.filter((e) => e.id !== id);
      persistEntries(updated);
      return updated;
    });
  }, []);

  return (
    <FlipStorageContext.Provider value={{ entries, addEntry, deleteEntry }}>
      {children}
    </FlipStorageContext.Provider>
  );
}

export function useFlipStorage() {
  const ctx = useContext(FlipStorageContext);
  if (!ctx) throw new Error("useFlipStorage must be used within FlipStorageProvider");
  return ctx;
}
